import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Shuffle } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { useNow, countdown } from "@/hooks/useNow";
import { StakeBox } from "@/components/StakeBox";

const HERO = "https://images.unsplash.com/photo-1518688248740-7c31f1a945c4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const flag = (c) => `https://flagcdn.com/w40/${c}.png`;

export const Ball = ({ n, hot }) => (
  <span className={`flex h-8 w-8 items-center justify-center rounded-full font-odds text-xs font-bold ${hot ? "bg-[#F59E0B] text-[#0B0E14]" : "bg-white text-[#0B0E14]"}`}>{n}</span>
);

const GameCard = ({ g, active, onClick, now }) => (
  <button data-testid={`lotto-game-${g.key}`} onClick={onClick}
    className={`flex items-center gap-3 rounded-lg border p-3 text-left transition-colors ${active ? "border-[#10B981] bg-[#10B981]/10" : "border-[#1E293B] bg-[#121824] hover:border-[#10B981]/50"}`}>
    <img src={flag(g.flag)} alt="" className="h-5 w-7 rounded-sm object-cover" />
    <div className="min-w-0 flex-1">
      <div className="truncate text-sm font-bold">{g.name}</div>
      <div className="text-[11px] text-slate-500">{g.drawn} from {g.pool}</div>
    </div>
    {g.next_draws[0] && <span className="font-odds text-xs text-[#F59E0B]">{countdown(g.next_draws[0].at, now)}</span>}
  </button>
);

export default function Lotto() {
  const qc = useQueryClient();
  const { refresh } = useAuth();
  const { currency } = useCurrency();
  const now = useNow(1000);
  const { data } = useQuery({ queryKey: ["lotto"], queryFn: () => api.get("/lotto").then((r) => r.data), refetchInterval: 60000 });
  const [key, setKey] = useState(null);
  const [nums, setNums] = useState([]);
  const [drawIdx, setDrawIdx] = useState(0);
  const g = (data || []).find((x) => x.key === key) || data?.[0];
  if (!g) return <div className="p-10 text-center text-sm text-slate-400">Loading draws…</div>;
  const draw = g.next_draws[drawIdx] || g.next_draws[0];
  const odds = nums.length ? g.odds[String(nums.length)] : null;

  const toggle = (n) =>
    setNums((p) => (p.includes(n) ? p.filter((x) => x !== n) : p.length >= g.max_pick ? p : [...p, n]));
  const quickPick = (k) => {
    const pool = Array.from({ length: g.pool }, (_, i) => i + 1).sort(() => Math.random() - 0.5);
    setNums(pool.slice(0, k));
  };
  const place = async (stake) => {
    try {
      await api.post("/lotto/bet", { draw_id: draw.id, numbers: nums, stake, currency });
      toast.success(`Lucky ${nums.length} placed on ${g.name}`);
      setNums([]);
      refresh();
      qc.invalidateQueries({ queryKey: ["bets"] });
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    }
  };

  return (
    <div className="space-y-6">
      <section className="grain relative overflow-hidden rounded-xl border border-[#1E293B]">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover opacity-35" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0E14] via-[#0B0E14]/85 to-transparent" />
        <div className="relative space-y-2 p-6 sm:p-8">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#F59E0B]">Lucky Numbers</span>
          <h1 className="font-display text-4xl font-black uppercase tracking-tight sm:text-5xl">World Lottos</h1>
          <p className="max-w-lg text-sm text-slate-300">Pick 1 to 5 numbers on SA Lotto, PowerBall, Daily Lotto, UK 49's, EuroMillions, US Powerball and more. All your numbers drawn — you win.</p>
        </div>
      </section>
      <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
        <div className="grid content-start gap-2 sm:grid-cols-2 lg:grid-cols-1">
          {data.map((x) => (
            <GameCard key={x.key} g={x} now={now} active={x.key === g.key} onClick={() => { setKey(x.key); setNums([]); setDrawIdx(0); }} />
          ))}
        </div>
        <div className="space-y-4">
          <section className="rounded-lg border border-[#1E293B] bg-[#121824] p-4">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <img src={flag(g.flag)} alt="" className="h-6 w-9 rounded-sm object-cover" />
                <div>
                  <h2 data-testid="lotto-title" className="font-display text-xl font-black uppercase">{g.name}</h2>
                  <div className="text-xs text-slate-400">{g.country} · {g.drawn} balls from 1–{g.pool}</div>
                </div>
              </div>
              <div className="flex gap-1">
                {g.next_draws.map((d, i) => (
                  <button key={d.id} data-testid={`lotto-draw-${i}`} onClick={() => setDrawIdx(i)}
                    className={`rounded-md px-3 py-1.5 text-xs ${drawIdx === i ? "bg-[#10B981] font-bold text-[#0B0E14]" : "bg-[#1A2234] text-slate-300"}`}>
                    {new Date(d.at).toLocaleString([], { weekday: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </button>
                ))}
              </div>
            </div>
            <div className="mb-3 flex flex-wrap gap-2 text-xs">
              {Object.entries(g.odds).map(([k, o]) => (
                <span key={k} className={`rounded px-2 py-1 ${String(nums.length) === k ? "bg-[#F59E0B]/20 text-[#F59E0B]" : "bg-[#1A2234] text-slate-300"}`}>
                  {k} ball{k > 1 ? "s" : ""} <span className="font-odds font-bold">{o.toLocaleString()}</span>
                </span>
              ))}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {Array.from({ length: g.pool }, (_, i) => i + 1).map((n) => (
                <button key={n} data-testid={`lotto-number-${n}`} onClick={() => toggle(n)}
                  className={`h-10 w-10 rounded-full font-odds text-sm font-bold transition-[background-color,transform] active:scale-90 ${
                    nums.includes(n) ? "bg-[#F59E0B] text-[#0B0E14] shadow-[0_0_12px_rgba(245,158,11,0.4)]" : "bg-[#1A2234] text-slate-200 hover:bg-[#242F46]"
                  }`}>
                  {n}
                </button>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {Array.from({ length: g.max_pick }, (_, i) => i + 1).map((k) => (
                <button key={k} data-testid={`lotto-quickpick-${k}`} onClick={() => quickPick(k)} className="flex items-center gap-1 rounded bg-[#1A2234] px-2.5 py-1 text-xs hover:bg-[#242F46]">
                  <Shuffle className="h-3 w-3" /> Quick {k}
                </button>
              ))}
              {nums.length > 0 && <button data-testid="lotto-clear" onClick={() => setNums([])} className="px-2 text-xs text-slate-400 hover:text-white">Clear</button>}
            </div>
          </section>
          <section className="grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-[#1E293B] bg-[#121824] p-4">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="font-display font-black uppercase">Your numbers</h3>
                {draw && <span data-testid="lotto-countdown" className="font-odds text-xs text-[#F59E0B]">Draw in {countdown(draw.at, now)}</span>}
              </div>
              <div data-testid="lotto-selected" className="mb-3 flex min-h-8 flex-wrap gap-1.5">
                {nums.length ? nums.map((n) => <Ball key={n} n={n} hot />) : <span className="text-sm text-slate-500">Pick up to {g.max_pick} numbers</span>}
              </div>
              {odds && <div className="mb-2 text-sm">Odds <span data-testid="lotto-odds" className="font-odds font-bold text-[#10B981]">{odds.toLocaleString()}</span></div>}
              <StakeBox odds={odds} disabled={!nums.length || !draw} onPlace={place} testid="lotto-stake" />
              <p className="mt-2 text-[11px] text-slate-500">Bets close 5 minutes before the draw.</p>
            </div>
            <div className="rounded-lg border border-[#1E293B] bg-[#121824] p-4">
              <h3 className="mb-3 font-display font-black uppercase">Latest draws</h3>
              <div className="space-y-3">
                {g.results.map((r) => (
                  <div key={r.id} data-testid={`lotto-result-${r.id}`}>
                    <div className="mb-1 text-[11px] text-slate-500">{new Date(r.at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}</div>
                    <div className="flex flex-wrap gap-1">
                      {r.numbers.map((n) => <Ball key={n} n={n} />)}
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#EF4444] font-odds text-xs font-bold">{r.bonus}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
